import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';
import BlogCTA from '../../components/BlogCTA';
import RelatedArticles from '../../components/RelatedArticles';

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Article',
      'headline': 'Australian Tax Invoice Requirements — What the ATO Requires in 2025',
      'description': 'What every Australian tax invoice must include under ATO rules — for GST-registered and non-GST businesses. Covers mandatory fields, the $82.50 threshold, and ABN requirements.',
      'url': 'https://www.invilic.com/blog/australia-tax-invoice-guide',
      'datePublished': '2026-06-01',
      'dateModified': '2026-06-01',
      'author': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
      'publisher': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
    },
    {
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'What is a tax invoice in Australia?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'A tax invoice is the document an Australian GST-registered business must issue for taxable supplies over $82.50 (including GST). It allows your customer to claim GST credits on their BAS. Without a valid tax invoice, your customer cannot claim the GST credit. For supplies $1,000 or more, a full tax invoice with the buyer\'s identity is required.' },
        },
        {
          '@type': 'Question',
          'name': 'What must be on an Australian tax invoice?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'An Australian tax invoice must contain: the words "Tax Invoice," your business name, your ABN, the invoice date, a description of the goods or services, the GST amount (either as a separate line or by stating "total price includes GST of [amount]"), and the total price. For supplies $1,000 or over, also include the buyer\'s name or ABN.' },
        },
        {
          '@type': 'Question',
          'name': 'Do I need to register for GST in Australia?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'You must register for GST if your annual turnover is $75,000 or more ($150,000 for non-profit organisations). Taxi and ride-sharing drivers must register regardless of turnover. Voluntary registration is available below $75,000 and may be worth considering if your customers are GST-registered businesses who can claim credits.' },
        },
        {
          '@type': 'Question',
          'name': 'What is the difference between a tax invoice and a regular invoice in Australia?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'A tax invoice is issued by a GST-registered business and includes GST details — your ABN, the GST amount, and the words "Tax Invoice." A regular invoice (or non-GST invoice) is issued by a business that is not GST-registered. It does not include GST and should not show a GST amount. Customers cannot claim GST credits from a non-GST invoice.' },
        },
        {
          '@type': 'Question',
          'name': 'Do I need to show my ABN on an Australian invoice?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Yes. All Australian businesses should include their ABN on invoices. If you do not include an ABN, customers may be required to withhold 47% of the payment as PAYG withholding tax and send it to the ATO. For tax invoices (issued by GST-registered businesses), the ABN is a mandatory field.' },
        },
      ],
    },
  ],
};

const faqs = schema['@graph'][1].mainEntity;

const taxInvoiceFields = [
  { field: 'The words "Tax Invoice" clearly displayed', note: 'Required by the ATO — must appear on the document' },
  { field: 'Your business name (or trading name)', note: null },
  { field: 'Your ABN (Australian Business Number)', note: '11-digit number issued by the ATO' },
  { field: 'The date the invoice was issued', note: null },
  { field: 'A brief description of the goods or services', note: 'Sufficient to identify what was supplied' },
  { field: 'The GST amount charged', note: 'Either as a separate line, or stated as "Total price includes GST of $X"' },
  { field: 'The total price including GST', note: null },
];

export default function AustraliaTaxInvoicePage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Australian Tax Invoice Requirements 2025 — ATO Rules Explained | Invilic Blog"
        description="What every Australian tax invoice must include: ABN, GST amount, 'Tax Invoice' label, and the $1,000 threshold rule. ATO-compliant invoice guide for small businesses."
        canonical="https://www.invilic.com/blog/australia-tax-invoice-guide"
        ogType="article"
        schema={schema}
      />
      <SeoNav />

      <main className="max-w-3xl mx-auto px-6 py-16">
        <header className="mb-10">
          <div className="flex items-center gap-2 mb-5 text-sm">
            <Link to="/blog" className="text-blue-600 dark:text-blue-400 hover:underline">Blog</Link>
            <ArrowRight className="w-3 h-3 text-gray-400" />
            <span className="text-gray-500 dark:text-gray-400">Australia</span>
          </div>
          <span className="inline-block bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 text-xs font-semibold px-2.5 py-1 rounded-full mb-4">Australia</span>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight mb-4">
            Australian Tax Invoice Requirements — What the ATO Requires in 2025
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm">June 1, 2026 · 6 min read</p>
        </header>

        <article className="text-gray-700 dark:text-gray-300 leading-relaxed">

          <p className="text-lg mb-6">
            An Australian tax invoice must display the words "Tax Invoice," your ABN, your business name, the date, a description of goods or services, and the GST amount. If the supply is valued at $1,000 or more, the buyer's name or ABN must also appear. Without these elements, your customer cannot claim a GST credit on their Business Activity Statement (BAS).
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">What Is a Tax Invoice?</h2>
          <p className="mb-4">
            A tax invoice is the formal document issued by a GST-registered Australian business for taxable supplies. It is your customer's evidence to claim GST credits with the ATO. Without a valid tax invoice, a GST-registered business customer cannot reclaim the GST they paid to you.
          </p>
          <p className="mb-4">
            You must issue a tax invoice for any taxable supply worth more than <strong className="font-semibold text-gray-900 dark:text-white">$82.50 including GST</strong>. For amounts under $82.50, a simple receipt is sufficient. Your customer can request a tax invoice within 28 days and you are legally required to provide one.
          </p>
          <p className="mb-6">
            If your business is not GST-registered (turnover under $75,000), you issue a regular invoice — without the words "Tax Invoice," without a GST line, and without an ABN-linked GST calculation. Simply invoice your fee.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Mandatory Fields on an Australian Tax Invoice</h2>
          <p className="mb-4">Every tax invoice must contain the following:</p>
          <div className="space-y-3 mb-6">
            {taxInvoiceFields.map(({ field, note }, i) => (
              <div key={field} className="flex items-start gap-3">
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 flex items-center justify-center text-xs font-bold mt-0.5">{i + 1}</div>
                <div className="text-sm">
                  <span className="font-medium text-gray-900 dark:text-white">{field}</span>
                  {note && <p className="text-gray-400 dark:text-gray-500 mt-0.5">{note}</p>}
                </div>
              </div>
            ))}
          </div>
          <div className="p-4 bg-sky-50 dark:bg-sky-900/20 border border-sky-100 dark:border-sky-800/30 rounded-xl mb-8 text-sm">
            <strong className="font-semibold text-gray-900 dark:text-white">For supplies $1,000 or more:</strong> You must also include the buyer's identity — their name, or their ABN. This extra requirement applies because the ATO uses tax invoices to trace large transactions.
          </div>

          <BlogCTA
            text="Create an ATO-compliant Australian tax invoice with your ABN, GST auto-calculated, and professional PDF. Free."
            to="/invoice-generator-australia"
            btnText="Create Australian Tax Invoice"
          />

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">GST Registration Threshold</h2>
          <p className="mb-4">
            You must register for GST if your annual GST turnover is <strong className="font-semibold text-gray-900 dark:text-white">$75,000 or more</strong> ($150,000 for non-profit organisations). Once registered, you must:
          </p>
          <ul className="space-y-2 mb-6 text-sm">
            {[
              'Charge GST (currently 10%) on all taxable supplies',
              'Issue tax invoices for supplies over $82.50',
              'Lodge a Business Activity Statement (BAS) — usually quarterly',
              'Remit the GST collected to the ATO, minus any GST credits you claim',
            ].map(item => (
              <li key={item} className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-sky-500 flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="mb-6">
            Taxi operators, ride-share drivers (Uber, Lyft, etc.), and ride-for-hire businesses must register for GST regardless of turnover level.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Tax Invoice vs Regular Invoice</h2>
          <div className="overflow-x-auto mb-8">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-900/50">
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800"></th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">Tax Invoice</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">Regular Invoice</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { label: 'Who issues it', tax: 'GST-registered business', reg: 'Non-GST-registered business' },
                  { label: 'Heading', tax: '"Tax Invoice"', reg: '"Invoice"' },
                  { label: 'ABN required', tax: 'Yes', reg: 'Recommended but not mandatory for GST' },
                  { label: 'GST line', tax: 'Required', reg: 'Not shown' },
                  { label: 'Customer can claim GST credit', tax: 'Yes', reg: 'No' },
                  { label: 'GST rate', tax: '10%', reg: 'N/A' },
                ].map(({ label, tax, reg }) => (
                  <tr key={label} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300 border border-gray-100 dark:border-gray-800">{label}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 border border-gray-100 dark:border-gray-800">{tax}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 border border-gray-100 dark:border-gray-800">{reg}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">ABN Withholding — Why Your ABN Matters</h2>
          <p className="mb-6">
            If you do not include an ABN on your invoice, the payer may be legally required to withhold <strong className="font-semibold text-gray-900 dark:text-white">47%</strong> of the payment as PAYG withholding tax and send it to the ATO. This is called "no ABN withholding." You can claim the withheld amount back in your tax return, but it is an unnecessary complication. Always include your ABN.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Record-Keeping for Australian Businesses</h2>
          <p className="mb-6">
            The ATO requires you to keep business records — including all tax invoices — for at least <strong className="font-semibold text-gray-900 dark:text-white">5 years</strong> from when you prepared or obtained them, or when transactions were completed, whichever is later. Electronic records are acceptable provided they are in a readable format and can be accessed on request.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none text-sm">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-5 pb-4 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>

          <BlogCTA
            text="Create an ATO-compliant Australian tax invoice — ABN field, 10% GST auto-calculated. Free, no account needed."
            to="/invoice-generator-australia"
            btnText="Create Australian Invoice Free"
          />

        </article>
        <RelatedArticles current="/blog/australia-tax-invoice-guide" />
      </main>
    </div>
  );
}
